import React from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  UserCheck,
  X,
  Database,
  FileText,
  Settings,
  LogOut,
  Megaphone
} from "lucide-react";
import logo from "../../assets/i2.png";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();

  const isActive = (paths = []) => {
    return paths.some((p) => location.pathname.startsWith(p));
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const baseItem =
    "flex items-center px-2.5 py-2 rounded-lg transition-all duration-200 text-sm";
  const idleItem =
    "text-gray-600 hover:bg-gray-50 hover:text-primary";
  const activeItem =
    "text-white bg-primary shadow-sm ring-1 ring-primary/20";

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-20 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          w-64 h-screen fixed left-0 top-0 z-30 transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          bg-white
          border-r border-gray-200 shadow-sm overflow-y-auto
        `}
        aria-label="Sidebar Navigation"
      >
        {/* Logo row */}
        <div className="p-2 border-b border-gray-200">
          <div className="flex items-center justify-center gap-2 min-w-0">
            <img
              src={logo}
              alt="BluTec PING"
              className="h-10 w-auto rounded-lg ring-1 ring-gray-200"
            />
          </div>
          <button
            onClick={toggleSidebar}
            className="text-gray-500 hover:text-primary lg:hidden p-1 rounded absolute top-2 right-2"
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-2 pb-2">
          <div className="px-2.5 mb-1.5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Main
            </p>
          </div>
          <ul className="space-y-0.5 px-2">
            <li>
              <Link
                to="/dashboard"
                className={`${baseItem} ${
                  isActive(["/dashboard"]) ? activeItem : idleItem
                }`}
                onClick={() => window.innerWidth < 1024 && toggleSidebar()}
              >
                <LayoutDashboard className="w-4 h-4 mr-2.5" />
                <span className="font-medium">Dashboard</span>
              </Link>
            </li>

            <li>
              <Link
                to="/main"
                className={`${baseItem} ${
                  isActive(["/main", "/instance"]) ? activeItem : idleItem
                }`}
                onClick={() => window.innerWidth < 1024 && toggleSidebar()}
              >
                <Database className="w-4 h-4 mr-2.5" />
                <span className="font-medium">Instances</span>
              </Link>
            </li>

            <li>
              <Link
                to="/docs"
                className={`${baseItem} ${
                  isActive(["/docs"]) ? activeItem : idleItem
                }`}
                onClick={() => window.innerWidth < 1024 && toggleSidebar()}
              >
                <FileText className="w-4 h-4 mr-2.5" />
                <span className="font-medium">API Docs</span>
              </Link>
            </li>
          </ul>
        </nav>

        {/* Bottom actions */}
        <div className="absolute bottom-12 left-0 right-0 p-2">
          <nav>
            <ul className="space-y-0.5 px-0">
              <li>
                <Link
                  to="/profile"
                  className={`${baseItem} ${
                    isActive(["/profile"]) ? activeItem : idleItem
                  }`}
                  onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                >
                  <UserCheck className="w-4 h-4 mr-2.5" />
                  <span className="font-medium">Profile</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/settings"
                  className={`${baseItem} ${
                    isActive(["/settings"]) ? activeItem : idleItem
                  }`}
                  onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                >
                  <Settings className="w-4 h-4 mr-2.5" />
                  <span className="font-medium">Settings</span>
                </Link>
              </li>

              <li>
                <button
                  onClick={handleLogout}
                  className={`${baseItem} ${idleItem} w-full text-left focus:outline-none`}
                  aria-label="Logout"
                >
                  <LogOut className="w-4 h-4 mr-2.5" />
                  <span className="font-medium">Logout</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-2">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-1.5">
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-[#01CC54]" />
              <p className="text-xs text-gray-600">
                BluTec PING v1.1
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
